import { FC, ReactNode, useCallback, useEffect } from 'react';
import { navigate } from 'gatsby';
import { useMeQuery } from 'lib/home/generated/queries';
import { openReplayTracker } from 'lib/util';
import { Loading } from '../../';
import { goToLogin } from '../../../util/helpers';

export interface PrivateLayoutProps {
  redirect?: string;
  loadingComponent?: ReactNode;
}

// NOTE: This seems to work fine, but if we want to, instead of doing this, we could use
// the "private route" method, which requires that we create client-only routes
// SEE: https://www.gatsbyjs.com/tutorial/authentication-tutorial/

export const PrivateLayout: FC<PrivateLayoutProps> = ({ children, loadingComponent, redirect }) => {
  const { data, isFetched, isLoading, isError, refetch } = useMeQuery(undefined, {
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const me = data?.me;

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (me) openReplayTracker.setUserID(me.email);
  }, [me]);

  const isReady = isFetched && !isLoading;
  const isAuthorized = !isError && !!me;

  const handleRouteAuth = useCallback(() => {
    const gettingStarted = window.location.toString().includes('getting-started');

    // If we haven't finished fetching the user data, return early.
    if (!isReady) return;

    // If the user is not authorized, redirect to the login page.
    if (!isAuthorized) return goToLogin(redirect);

    // If the user is still onboarding, redirect to the onboarding flow.
    if (!gettingStarted && (!me.subscription || me?.subscription.status === 'ONBOARDING'))
      return navigate('/getting-started/redirect');

    // Otherwise, do nothing...
  }, [isReady, isAuthorized, me, redirect]);

  useEffect(() => {
    handleRouteAuth();
  }, [handleRouteAuth]);

  if (!isReady && loadingComponent) return loadingComponent;
  if (!isReady && !loadingComponent && typeof children !== 'function') return <Loading />;

  // NOTE: We don't need to render anything for private routes on SSR.
  if (typeof window === 'undefined') return null;

  if (typeof children === 'function') return children({ isAuthorized, isReady, me });

  return children;
};
